/**
 * ProcessorWorkloadTable
 *
 * Compact, sortable, filterable table view of Auto Assignment processor
 * workloads. Replaces the previous card list that collapsed everyone past
 * the 5th processor behind "+X more processors".
 *
 * Operationally focused for Team Leaders managing 10-20+ processors:
 * - All processors visible (no collapsing)
 * - Default sort: Stuck-first, then Utilization DESC
 * - Search + quick filter chips (Stuck / Inactive / Manual / Over Capacity)
 * - Row click expands inline to show full eligibility scope
 * - Sticky header + sort indicators per column
 * - Imperatively controllable filter (parent's System Health "View" CTA
 *   auto-applies the Stuck filter via the `forceFilter` ref handle)
 *
 * UX-only: no backend, schema, or API contract changes. Uses the existing
 * `summary.processors[]` payload from /api/auto-assignment/dashboard/summary.
 */
import React, { useState, useMemo, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  Users,
  AlertTriangle,
  Zap,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import UsersPagination from '../UsersPagination';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Status pill — same color language as the legacy cards. */
const StatusPill = ({ proc }) => {
  if (proc.is_work_active) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-600">
      <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
      {proc.work_status || 'Inactive'}
    </span>
  );
};

const utilColor = (u) => {
  if (u >= 100) return 'text-red-600';
  if (u >= 90) return 'text-red-600';
  if (u >= 70) return 'text-orange-600';
  return 'text-green-600';
};

const SortHeader = ({ label, field, sort, onSort, align = 'left', className = '' }) => {
  const active = sort.field === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-white/90 hover:text-white ${
        align === 'right' ? 'ml-auto' : ''
      } ${className}`}
      data-testid={`processor-sort-${field}`}
    >
      <span>{label}</span>
      {active ? (
        sort.dir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />
      ) : (
        <span className="h-3 w-3" />
      )}
    </button>
  );
};

const CHIP_TONES = {
  slate: { active: 'border-slate-600 bg-slate-600 text-white', idle: 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100', badgeActive: 'bg-white/25 text-white', badgeIdle: 'bg-slate-200/80 text-slate-600' },
  rose: { active: 'border-rose-500 bg-rose-500 text-white', idle: 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100', badgeActive: 'bg-white/25 text-white', badgeIdle: 'bg-rose-100 text-rose-700' },
  amber: { active: 'border-amber-500 bg-amber-500 text-white', idle: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100', badgeActive: 'bg-white/25 text-white', badgeIdle: 'bg-amber-100 text-amber-700' },
  violet: { active: 'border-violet-500 bg-violet-500 text-white', idle: 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100', badgeActive: 'bg-white/25 text-white', badgeIdle: 'bg-violet-100 text-violet-700' },
};

const FilterChip = ({ label, active, onToggle, count, testId, tone = 'slate' }) => {
  const t = CHIP_TONES[tone] || CHIP_TONES.slate;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all duration-150 hover:-translate-y-px ${
        active ? t.active : t.idle
      }`}
      data-testid={testId}
    >
      {label}
      {typeof count === 'number' && (
        <span
          className={`rounded-full px-1.5 text-[10px] font-semibold ${
            active ? t.badgeActive : t.badgeIdle
          }`}
        >
          {count}
      </span>
      )}
    </button>
  );
};

const STATUS_WEIGHT = {
  active: 0,
  break: 1,
  downtime: 2,
  logged_out: 3,
  not_reported: 4,
  inactive: 5,
};

const ProcessorWorkloadTable = forwardRef(({ processors = [], capacity = {}, canBulkEdit = false, onBulkSetStates }, ref) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filters, setFilters] = useState({
    stuck: false,
    inactive: false,
    manual: false,
    overcap: false,
  });
  // Default sort: Stuck-first, then Utilization DESC. The `stuck` field is
  // a virtual primary key that always pulls stuck rows to the top.
  const [sort, setSort] = useState({ field: 'utilization', dir: 'desc' });
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Back to page 1 whenever the visible set changes
  useEffect(() => {
    setPage(1);
  }, [search, filters, pageSize]);

  // Parent uses this to flip the "Stuck" chip from outside (System Health "View")
  useImperativeHandle(ref, () => ({
    applyStuckFilter: () => {
      setFilters({ stuck: true, inactive: false, manual: false, overcap: false });
      setSearch('');
    },
    clearAllFilters: () => {
      setFilters({ stuck: false, inactive: false, manual: false, overcap: false });
      setSearch('');
    },
  }), []);

  const counts = useMemo(() => ({
    stuck: processors.filter((p) => p.is_stuck).length,
    inactive: processors.filter((p) => !p.is_work_active).length,
    manual: processors.filter((p) => !p.auto_enabled).length,
    overcap: processors.filter((p) => (p.utilization || 0) >= 100).length,
  }), [processors]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return processors.filter((p) => {
      if (q && !(p.name || '').toLowerCase().includes(q)) return false;
      if (filters.stuck && !p.is_stuck) return false;
      if (filters.inactive && p.is_work_active) return false;
      if (filters.manual && p.auto_enabled) return false;
      if (filters.overcap && (p.utilization || 0) < 100) return false;
      return true;
    });
  }, [processors, search, filters]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const cmpField = (a, b) => {
      const dir = sort.dir === 'desc' ? -1 : 1;
      switch (sort.field) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '') * dir;
        case 'status': {
          const av = STATUS_WEIGHT[a.work_status] ?? 9;
          const bv = STATUS_WEIGHT[b.work_status] ?? 9;
          return (av - bv) * dir;
        }
        case 'wip':
          return ((a.active_orders || 0) - (b.active_orders || 0)) * dir;
        case 'utilization':
          return ((a.utilization || 0) - (b.utilization || 0)) * dir;
        case 'done':
          return ((a.completed_today || 0) - (b.completed_today || 0)) * dir;
        default:
          return 0;
      }
    };
    // Stuck-first is always honored, then secondary user-controlled sort
    arr.sort((a, b) => {
      if (a.is_stuck !== b.is_stuck) return a.is_stuck ? -1 : 1;
      return cmpField(a, b);
    });
    return arr;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize]
  );

  const handleSort = (field) => {
    setSort((prev) => {
      if (prev.field === field) {
        return { field, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
      }
      // Sensible defaults per column
      const defaultDir = field === 'name' || field === 'status' ? 'asc' : 'desc';
      return { field, dir: defaultDir };
    });
  };

  const toggleFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const activeCount = capacity?.active_work_processors || 0;
  const enabledCount = capacity?.enabled_processors || 0;

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectedProcessors = useMemo(
    () => processors.filter((p) => selectedIds.has(p.id)),
    [processors, selectedIds]
  );
  const allVisibleSelected = paged.length > 0 && paged.every((p) => selectedIds.has(p.id));
  const toggleSelectAllVisible = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) paged.forEach((p) => next.delete(p.id));
      else paged.forEach((p) => next.add(p.id));
      return next;
    });

  return (
    <Card className="lg:col-span-2 rounded-2xl border-slate-200/80 shadow-sm overflow-hidden" data-testid="processor-workload-table">
      <CardHeader className="px-5 pt-5 pb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md shadow-blue-500/25">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-xl font-semibold tracking-tight text-slate-900">Processor Workloads</CardTitle>
              <CardDescription className="mt-0.5">
                Sortable view of every processor. Only{' '}
                <span className="font-medium text-gray-700">Active</span> processors receive
                auto-assignments.
              </CardDescription>
            </div>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search processors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 rounded-xl bg-slate-50/60 pl-9 transition-colors focus:bg-white"
              data-testid="processor-search-input"
            />
          </div>
        </div>

        {/* Status chips + quick filters */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700" data-testid="processor-active-count">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {activeCount}/{enabledCount} Active
          </span>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
            {processors.length} Total
          </span>
          <span className="mx-1 h-4 w-px bg-slate-200" aria-hidden="true" />
          <FilterChip
            label="Stuck"
            count={counts.stuck}
            active={filters.stuck}
            onToggle={() => toggleFilter('stuck')}
            testId="processor-filter-stuck"
            tone="rose"
          />
          <FilterChip
            label="Inactive"
            count={counts.inactive}
            active={filters.inactive}
            onToggle={() => toggleFilter('inactive')}
            testId="processor-filter-inactive"
            tone="slate"
          />
          <FilterChip
            label="Manual"
            count={counts.manual}
            active={filters.manual}
            onToggle={() => toggleFilter('manual')}
            testId="processor-filter-manual"
            tone="violet"
          />
          <FilterChip
            label="Over Capacity"
            count={counts.overcap}
            active={filters.overcap}
            onToggle={() => toggleFilter('overcap')}
            testId="processor-filter-overcap"
            tone="amber"
          />
          {(filters.stuck || filters.inactive || filters.manual || filters.overcap || search) && (
            <button
              type="button"
              onClick={() => {
                setFilters({ stuck: false, inactive: false, manual: false, overcap: false });
                setSearch('');
              }}
              className="ml-1 text-xs text-gray-500 underline hover:text-gray-700"
              data-testid="processor-filter-clear"
            >
              Clear all
            </button>
          )}
        </div>

        {canBulkEdit && selectedProcessors.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3" data-testid="processor-bulk-action-bar">
            <span className="text-xs font-medium text-blue-800">
              {selectedProcessors.length} processor(s) selected
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onBulkSetStates?.(selectedProcessors)}
                className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                data-testid="processor-bulk-set-states"
              >
                Set Eligible States
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-gray-500 underline hover:text-gray-700"
                data-testid="processor-bulk-clear-selection"
              >
                Clear selection
              </button>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0">
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            {/* Aug 2026 — Title Flow blue header band with white text */}
            <thead className="sticky top-0 z-10 bg-blue-600 text-white">
              <tr className="border-b border-blue-500">
                {canBulkEdit && (
                  <th className="w-8 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      className="h-3.5 w-3.5 cursor-pointer"
                      data-testid="processor-select-all"
                    />
                  </th>
                )}
                <th className="w-8 px-3 py-3"></th>
                <th className="px-4 py-3 text-left">
                  <SortHeader label="Name" field="name" sort={sort} onSort={handleSort} />
                </th>
                <th className="px-4 py-3 text-left">
                  <SortHeader label="Status" field="status" sort={sort} onSort={handleSort} />
                </th>
                <th className="px-4 py-3 text-left">
                  <SortHeader label="WIP" field="wip" sort={sort} onSort={handleSort} />
                </th>
                <th className="px-4 py-3 text-left">
                  <SortHeader label="Util %" field="utilization" sort={sort} onSort={handleSort} />
                </th>
                <th className="px-4 py-3 text-left">
                  <SortHeader label="Done Today" field="done" sort={sort} onSort={handleSort} />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-white/90">
                  Scope
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-white/90">
                  Flags
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={canBulkEdit ? 9 : 8} className="px-3 py-8 text-center text-sm text-gray-500" data-testid="processor-table-empty">
                    No processors match the current filters.
                  </td>
                </tr>
              )}
              {paged.map((proc) => {
                const isOpen = expandedId === proc.id;
                const hasNoStates = (proc.eligible_states?.length || 0) === 0;
                const scopeSummary = [
                  hasNoStates
                    ? '⚠ No eligible states'
                    : `${proc.eligible_states.length} state${proc.eligible_states.length === 1 ? '' : 's'}`,
                  (proc.eligible_client_codes?.length || 0) === 0
                    ? 'All clients'
                    : `${proc.eligible_client_codes.length} client${proc.eligible_client_codes.length === 1 ? '' : 's'}`,
                  (proc.eligible_product_ids?.length || 0) === 0
                    ? 'All products'
                    : `${proc.eligible_product_ids.length} prod${proc.eligible_product_ids.length === 1 ? '' : 's'}`,
                ].join(' · ');

                return (
                  <React.Fragment key={proc.id}>
                    <tr
                      className={`cursor-pointer border-b border-slate-100 transition-colors hover:bg-blue-50/50 ${
                        proc.is_stuck ? 'bg-red-50/40' : !proc.is_work_active ? 'bg-gray-50/60' : ''
                      }`}
                      onClick={() => setExpandedId(isOpen ? null : proc.id)}
                      data-testid={`processor-table-row-${proc.id}`}
                    >
                      {canBulkEdit && (
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(proc.id)}
                            onChange={() => toggleSelect(proc.id)}
                            className="h-3.5 w-3.5 cursor-pointer"
                            data-testid={`processor-select-${proc.id}`}
                          />
                        </td>
                      )}
                      <td className="px-3 py-3 text-gray-400">
                        <ChevronRight
                          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{proc.name}</td>
                      <td className="px-4 py-3">
                        <StatusPill proc={proc} />
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="flex items-center gap-2">
                          <span className="font-medium tabular-nums">
                            {proc.active_orders}/{proc.wip_limit}
                          </span>
                          <Progress
                            value={Math.min(proc.utilization || 0, 100)}
                            className="h-1.5 w-16"
                          />
                        </div>
                      </td>
                      <td className={`px-4 py-3 font-semibold tabular-nums ${utilColor(proc.utilization || 0)}`}>
                        {proc.utilization}%
                      </td>
                      <td className="px-4 py-3 tabular-nums text-green-600">
                        {proc.completed_today}
                      </td>
                      <td className={`px-4 py-3 text-xs ${hasNoStates && proc.auto_enabled ? 'font-semibold text-red-600' : 'text-gray-500'}`} data-testid={`processor-scope-${proc.id}`}>{scopeSummary}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1">
                          {proc.is_stuck && (
                            <Badge variant="destructive" className="text-[10px]">
                              <AlertTriangle className="mr-0.5 h-3 w-3" />
                              Stuck
                            </Badge>
                          )}
                          {proc.auto_enabled && hasNoStates && (
                            <Badge variant="destructive" className="text-[10px]" data-testid={`processor-no-states-badge-${proc.id}`}>
                              <AlertTriangle className="mr-0.5 h-3 w-3" />
                              No states
                            </Badge>
                          )}
                          {proc.auto_enabled ? (
                            <Badge variant="outline" className="border-blue-300 text-[10px] text-blue-600">
                              <Zap className="mr-0.5 h-3 w-3" />
                              Auto
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-gray-300 text-[10px] text-gray-500">
                              Manual
                            </Badge>
                          )}
                          {(proc.utilization || 0) >= 100 && !proc.is_stuck && (
                            <Badge variant="outline" className="border-red-300 text-[10px] text-red-600">
                              Over cap
                            </Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b bg-gray-50/40" data-testid={`processor-table-expand-${proc.id}`}>
                        <td colSpan={canBulkEdit ? 9 : 8} className="px-6 py-3">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Eligible States
                              </div>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {(proc.eligible_states?.length || 0) === 0 ? (
                                  <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-600">
                                    No eligible states — auto-assignment disabled
                                  </span>
                                ) : (
                                  proc.eligible_states.map((s) => (
                                    <span
                                      key={s}
                                      className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700"
                                    >
                                      {s}
                                    </span>
                                  ))
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Eligible Clients
                              </div>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {(proc.eligible_client_codes?.length || 0) === 0 ? (
                                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs italic text-gray-500">
                                    All clients
                                  </span>
                                ) : (
                                  proc.eligible_client_codes.map((c) => (
                                    <span
                                      key={c}
                                      className="rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-700"
                                    >
                                      {c}
                                    </span>
                                  ))
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Eligible Products
                              </div>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {(proc.eligible_product_ids?.length || 0) === 0 ? (
                                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs italic text-gray-500">
                                    All products
                                  </span>
                                ) : (
                                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-700">
                                    {proc.eligible_product_ids.length} product
                                    {proc.eligible_product_ids.length === 1 ? '' : 's'} configured
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-3 border-t pt-2 text-xs text-gray-500">
                            <span>
                              WIP limit: <span className="font-medium text-gray-700">{proc.wip_limit}</span>
                            </span>
                            <span>
                              Daily target:{' '}
                              <span className="font-medium text-gray-700">
                                {proc.daily_target ? proc.daily_target : 'No cap'}
                              </span>
                            </span>
                            {typeof proc.stuck_threshold_count === 'number' && (
                              <span>
                                Stuck threshold:{' '}
                                <span className="font-medium text-gray-700">
                                  {proc.stuck_threshold_count} order
                                  {proc.stuck_threshold_count === 1 ? '' : 's'} /{' '}
                                  {proc.stuck_threshold_hours}h
                                </span>
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <UsersPagination
          page={safePage}
          pageSize={pageSize}
          totalPages={totalPages}
          totalItems={sorted.length}
          onPageChange={setPage}
          itemLabel="processors"
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={setPageSize}
          testIdPrefix="processor-pagination"
        />
        <p className="mt-2 text-xs text-gray-500" data-testid="processor-table-footer">
          {sorted.length} of {processors.length} processors match · Sorted by{' '}
          <span className="font-medium">{sort.field}</span> ({sort.dir}) · Stuck always first
        </p>
      </CardContent>
    </Card>
  );
});

ProcessorWorkloadTable.displayName = 'ProcessorWorkloadTable';

export default ProcessorWorkloadTable;
